/**
 * Step-by-Step Mode Hook
 *
 * Manages the state for step-by-step response mode.
 * When enabled, instructs the AI to provide one step at a time with detailed explanations.
 * Can be initialized from chatbot config for per-chatbot settings.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect } from "react";
export type StepByStepMode = "off" | "with-explanation" | "no-explanation";
type StepByStepConfig = {
  step_by_step_with_explanation?: boolean;
  step_by_step_no_explanation?: boolean;
};
type UseStepByStepModeResult = {
  stepByStepMode: StepByStepMode;
  isStepByStepWithExplanation: boolean;
  isStepByStepNoExplanation: boolean;
  toggleStepByStepWithExplanation: () => void;
  toggleStepByStepNoExplanation: () => void;
  getSystemPromptAddition: () => string;
};

/**
 * Convert chatbot config to step-by-step mode
 */
function configToMode(config?: StepByStepConfig | null): StepByStepMode {
  if (config?.step_by_step_with_explanation) return "with-explanation";
  if (config?.step_by_step_no_explanation) return "no-explanation";
  return "off";
}

/**
 * Hook to manage step-by-step response mode
 * @param chatbotConfig - Optional chatbot config to initialize from
 */
export function useStepByStepMode(chatbotConfig) {
  const $ = _c(16);
  let t0;
  if ($[0] !== chatbotConfig) {
    t0 = () => configToMode(chatbotConfig);
    $[0] = chatbotConfig;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [stepByStepMode, setStepByStepMode] = useState(t0);
  let t1;
  if ($[2] !== chatbotConfig) {
    t1 = () => {
      const newMode = configToMode(chatbotConfig);
      console.log("[useStepByStepMode] Syncing with chatbot config:", chatbotConfig, "-> mode:", newMode);
      setStepByStepMode(newMode);
    };
    $[2] = chatbotConfig;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = chatbotConfig?.step_by_step_with_explanation;
  const t3 = chatbotConfig?.step_by_step_no_explanation;
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = [t2, t3];
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t1, t4);
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setStepByStepMode(_temp);
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const toggleStepByStepWithExplanation = t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setStepByStepMode(_temp2);
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const toggleStepByStepNoExplanation = t6;
  let t7;
  if ($[9] !== stepByStepMode) {
    t7 = () => {
      switch (stepByStepMode) {
        case "with-explanation":
          {
            return "\n\nIMPORTANT: Step-by-Step Mode is ENABLED.\n\nYou must respond with ONLY ONE STEP at a time. Follow these rules strictly:\n\n1. Provide ONLY the next single step in the process\n2. Include a detailed explanation of WHY this step is necessary\n3. Explain WHAT this step accomplishes\n4. Stop after explaining this ONE step\n5. Wait for the user to ask for the next step before continuing\n\nFormat your response as:\n**Step [number]: [Brief title]**\n\n[Detailed explanation of what this step does and why it's important]\n\n[The actual step content, code, or instructions]\n\n**Ready for next step:** Ask me to continue when you're ready for the next step.\n\nRemember: DO NOT provide multiple steps. DO NOT continue beyond one step. WAIT for user confirmation.";
          }
        case "no-explanation":
          {
            return "\n\nIMPORTANT: Step-by-Step Mode (No Explanation) is ENABLED.\n\nYou must respond with ONLY ONE STEP at a time with NO explanation. Follow these rules strictly:\n\n1. Provide ONLY the next single step\n2. Do NOT include explanations\n3. Do NOT include commentary\n4. Just provide the minimal step content/code/instruction\n5. Stop after providing this ONE step\n6. Wait for the user to ask for the next step before continuing\n\nFormat: Just provide the step content directly without extra text.\n\nRemember: DO NOT provide multiple steps. DO NOT add explanations. WAIT for user confirmation.";
          }
        case "off":
        default:
          {
            return "";
          }
      }
    };
    $[9] = stepByStepMode;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const getSystemPromptAddition = t7;
  const t8 = stepByStepMode === "with-explanation";
  const t9 = stepByStepMode === "no-explanation";
  let t10;
  if ($[11] !== getSystemPromptAddition || $[12] !== stepByStepMode || $[13] !== t8 || $[14] !== t9) {
    t10 = {
      stepByStepMode,
      isStepByStepWithExplanation: t8,
      isStepByStepNoExplanation: t9,
      toggleStepByStepWithExplanation,
      toggleStepByStepNoExplanation,
      getSystemPromptAddition
    };
    $[11] = getSystemPromptAddition;
    $[12] = stepByStepMode;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
}
function _temp2(current_0) {
  if (current_0 === "no-explanation") {
    console.log("Step-by-step mode (no explanation): OFF");
    return "off";
  } else {
    console.log("Step-by-step mode (no explanation): ON");
    return "no-explanation";
  }
}
function _temp(current) {
  if (current === "with-explanation") {
    console.log("Step-by-step mode (with explanation): OFF");
    return "off";
  } else {
    console.log("Step-by-step mode (with explanation): ON");
    return "with-explanation";
  }
}